// Beheer van de labellijst (SPEC 27okt 6–7; in de UI heten tags nu "labels", want "taggen" betekent
// een collega taggen). Enkel voor rollen met beheer (Ervin, Timen). Vaste labels toevoegen, hernoemen
// en verwijderen; vrije labels vast maken of opruimen. Automatische labels zet en verwijdert de tool
// zelf: die kan je hier niet wijzigen.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '../components/AppShell';
import { useToast } from '../components/Toast';
import { api, ApiError, type TagLijstRij } from '../lib/api';

type Soort = 'vast' | 'vrij' | 'automatisch';
const soortVan = (t: TagLijstRij): Soort => (t.automatisch ? 'automatisch' : t.vast ? 'vast' : 'vrij');
const SOORT_VOLGORDE: Record<Soort, number> = { vast: 0, vrij: 1, automatisch: 2 };
const SOORT_UITLEG: Record<Soort, string> = {
  vast: 'Staat in de vaste lijst die iedereen voorgesteld krijgt bij het toevoegen van een label.',
  vrij: 'Vrij toegevoegd op een project. Maak het vast of ruim het op om wildgroei te vermijden.',
  automatisch: 'De tool zet en verwijdert dit label zelf, volgens de oorzaak. Niet te wijzigen.',
};

function foutTekst(e: unknown, standaard: string): string {
  const p = e instanceof ApiError ? (e.payload as { error?: string } | null) : null;
  return p?.error || standaard;
}

export function TagsBeheer() {
  const qc = useQueryClient();
  const toast = useToast();
  const [nieuw, setNieuw] = useState('');
  const [bewerk, setBewerk] = useState<{ id: string; naam: string } | null>(null);
  const q = useQuery({ queryKey: ['tags'], queryFn: () => api.dossier.tags() });
  const tags = [...(q.data?.tags ?? [])].sort((a, b) => SOORT_VOLGORDE[soortVan(a)] - SOORT_VOLGORDE[soortVan(b)] || a.naam.localeCompare(b.naam, 'nl'));

  const verversen = () => {
    qc.invalidateQueries({ queryKey: ['tags'] });
    qc.invalidateQueries({ queryKey: ['projects'] });
    qc.invalidateQueries({ queryKey: ['projectniveau'] });
    qc.invalidateQueries({ queryKey: ['project'] });
  };
  const maak = useMutation({
    mutationFn: (naam: string) => api.dossier.maakVasteTag(naam),
    onSuccess: (_r, naam) => { toast.success(`Vast label "${naam.trim()}" staat in de lijst`); setNieuw(''); verversen(); },
    onError: (e) => toast.error(foutTekst(e, 'Label toevoegen mislukt')),
  });
  const hernoem = useMutation({
    mutationFn: (v: { id: string; naam: string }) => api.dossier.hernoemTag(v.id, v.naam),
    onSuccess: () => { setBewerk(null); verversen(); },
    onError: (e) => toast.error(foutTekst(e, 'Hernoemen mislukt')),
  });
  const verwijder = useMutation({
    mutationFn: (id: string) => api.dossier.verwijderTagUitLijst(id),
    onSuccess: () => { toast.success('Label verwijderd'); verversen(); },
    onError: (e) => toast.error(foutTekst(e, 'Verwijderen mislukt')),
  });

  const naamGeldig = (n: string) => n.trim().length >= 2 && n.trim().length <= 40;
  const vraagVerwijderen = (t: TagLijstRij) => {
    const op = t.gebruik ? ` Het verdwijnt van ${t.gebruik} project${t.gebruik === 1 ? '' : 'en'}.` : '';
    if (confirm(`Label "${t.naam}" verwijderen uit de lijst?${op}`)) verwijder.mutate(t.id);
  };

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">Labels</h1>
        <p className="page-subtitle">Vaste lijst voor alle projecten. Vrije labels blijven toegestaan, met autocomplete op deze lijst.</p>
      </div>

      <form
        className="card-flat mb-5"
        onSubmit={(e) => { e.preventDefault(); if (naamGeldig(nieuw)) maak.mutate(nieuw); }}
      >
        <label htmlFor="nieuwe-tag" className="field-label">Nieuw vast label</label>
        <div className="flex flex-wrap gap-3">
          <input id="nieuwe-tag" className="input flex-1 min-w-[240px]" maxLength={40} placeholder="bv. wacht op facturatie" value={nieuw} onChange={(e) => setNieuw(e.target.value)} />
          <button type="submit" className="btn-accent" disabled={!naamGeldig(nieuw) || maak.isPending}>
            {maak.isPending ? 'Toevoegen…' : 'Vast label toevoegen'}
          </button>
        </div>
        <p className="field-hint">2 tot 40 tekens. Bestaat het label al als vrij label, dan wordt het vast.</p>
      </form>

      <div className="card-flat overflow-x-auto">
        {q.isLoading ? <p className="text-secondary text-sm">Laden…</p> : q.isError ? (
          <p className="text-danger text-sm">De labellijst kon niet geladen worden.</p>
        ) : tags.length === 0 ? <p className="text-secondary text-sm">Nog geen labels.</p> : (
          <table className="w-full">
            <thead>
              <tr className="thead-row">
                <th className="text-left px-3 py-2">Label</th>
                <th className="text-left px-3 py-2">Soort</th>
                <th className="text-right px-3 py-2">Projecten</th>
                <th className="text-right px-3 py-2"><span className="sr-only">Acties</span></th>
              </tr>
            </thead>
            <tbody>
              {tags.map((t) => {
                const soort = soortVan(t);
                const isBewerk = bewerk?.id === t.id;
                return (
                  <tr key={t.id} className="border-b border-lightgrey last:border-b-0 text-sm align-middle">
                    <td className="px-3 py-2">
                      {isBewerk ? (
                        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (naamGeldig(bewerk.naam)) hernoem.mutate(bewerk); }}>
                          <label htmlFor={`tag-${t.id}`} className="sr-only">Nieuwe naam voor {t.naam}</label>
                          <input id={`tag-${t.id}`} autoFocus className="input py-1.5 w-56" maxLength={40} value={bewerk.naam}
                            onChange={(e) => setBewerk({ id: t.id, naam: e.target.value })}
                            onKeyDown={(e) => { if (e.key === 'Escape') setBewerk(null); }} />
                          <button type="submit" className="btn-primary btn-sm" disabled={!naamGeldig(bewerk.naam) || hernoem.isPending}>Opslaan</button>
                          <button type="button" className="btn-ghost btn-sm" onClick={() => setBewerk(null)}>Annuleren</button>
                        </form>
                      ) : (
                        <span className="font-semibold">{t.naam}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={soort === 'automatisch'
                          ? 'chip bg-accent-50 text-accent-700'
                          : soort === 'vast' ? 'chip' : 'chip-neutral'}
                        title={SOORT_UITLEG[soort]}
                      >
                        {soort}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{t.gebruik}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {soort === 'automatisch' ? (
                        <span className="text-xs text-secondary">automatisch beheerd</span>
                      ) : !isBewerk && (
                        <span className="inline-flex gap-2">
                          {soort === 'vrij' && (
                            <button type="button" className="btn-ghost btn-sm" disabled={maak.isPending} onClick={() => maak.mutate(t.naam)}
                              title="Opnemen in de vaste lijst">Maak vast</button>
                          )}
                          <button type="button" className="btn-ghost btn-sm" onClick={() => setBewerk({ id: t.id, naam: t.naam })}>Hernoemen</button>
                          <button type="button" className="btn-ghost btn-sm text-danger-700 hover:bg-danger-100 hover:border-danger/40" disabled={verwijder.isPending}
                            onClick={() => vraagVerwijderen(t)}>Verwijderen</button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </AppShell>
  );
}
